/**
 * Quarterly "Rules for using Tyashin" notice (IT Rules 3(1)(c)/(ca)).
 *
 * Shown on sign-in while `user.userRulesNotice.due` (from /auth/me). It does
 * not block the app: "Remind me later", Escape or the close button hide it for
 * this browser session and leave a slim banner until the user confirms. "I
 * understand" records the acknowledgement (one write server-side) and the
 * server's new status replaces the one on the user object.
 *
 * The text comes from GET /user-rules-notice, so the backend module
 * (aapastech-backend/src/lib/legal/user-rules-notice.ts) is the only copy.
 * Hidden in ghost sessions: an admin viewing as someone must not confirm for them.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { ScrollText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { cn } from '@/lib/utils';
import { isGhostSession, useAuthStore } from '@/stores/auth';
import type { UserRulesNoticeLanguage } from '@/types';

const LANGUAGES: { code: UserRulesNoticeLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
];
const LANGUAGE_KEY = 'user-rules-notice:lang';

// Browser storage is a convenience here (remembered language, "later" for this
// tab session). It can throw in private windows; the notice must still work.
function readStorage(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Not persisted; the in-memory state still applies for this page view.
  }
}

export function UserRulesNotice() {
  const user = useAuthStore((s) => s.user);
  const status = user?.userRulesNotice;
  const due = Boolean(status?.due) && !isGhostSession();
  const laterKey = user && status ? `user-rules-notice:later:${user.id}:${status.version}` : '';

  const [dismissed, setDismissed] = useState(
    () => Boolean(laterKey) && readStorage(() => sessionStorage, laterKey) === '1'
  );
  const [lang, setLang] = useState<UserRulesNoticeLanguage>(() =>
    readStorage(() => localStorage, LANGUAGE_KEY) === 'hi' ? 'hi' : 'en'
  );
  const titleRef = useRef<HTMLHeadingElement>(null);
  const queryClient = useQueryClient();

  const { data: notice } = useQuery({
    queryKey: qk.userRulesNotice(),
    queryFn: async () => (await apiHelpers.getUserRulesNotice()).data.data!,
    enabled: due,
    staleTime: 60 * 60 * 1000,
  });

  const acknowledge = useMutation({
    mutationFn: async () =>
      (await apiHelpers.acknowledgeUserRulesNotice({ version: notice!.version, language: lang }))
        .data.data!,
    onSuccess: (next) => {
      const current = useAuthStore.getState().user;
      if (current) useAuthStore.setState({ user: { ...current, userRulesNotice: next } });
    },
    onError: (err) => {
      const code = (err as AxiosError<{ error?: { code?: string } }>).response?.data?.error?.code;
      if (code === 'NOTICE_VERSION_STALE') {
        // The wording changed while this tab was open: load the new text.
        void queryClient.invalidateQueries({ queryKey: qk.userRulesNotice() });
        void useAuthStore.getState().refreshUser();
        return;
      }
      toast.error(apiErrorMessage(err, 'Could not save your confirmation. Please try again.'));
    },
  });

  if (!due || !notice || notice.version !== status?.version) return null;

  const t = notice.content[lang];
  const grievanceUrl = lang === 'hi' ? notice.links.grievanceHi : notice.links.grievance;

  const chooseLanguage = (code: UserRulesNoticeLanguage) => {
    setLang(code);
    writeStorage(() => localStorage, LANGUAGE_KEY, code);
  };
  const remindLater = () => {
    setDismissed(true);
    writeStorage(() => sessionStorage, laterKey, '1');
  };

  return (
    <>
      {dismissed && (
        <div
          role="region"
          aria-label={t.title}
          lang={lang}
          className="flex flex-wrap items-center justify-between gap-2 border-b border-sky-300 bg-sky-50 px-4 py-2 dark:border-sky-800 dark:bg-sky-950/30"
        >
          <p className="flex items-center gap-2 text-sm font-medium text-sky-900 dark:text-sky-100">
            <ScrollText className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t.bannerText}
          </p>
          <Button size="sm" variant="outline" onClick={() => setDismissed(false)}>
            {t.bannerAction}
          </Button>
        </div>
      )}

      <Dialog
        open={!dismissed}
        onOpenChange={(open) => {
          if (!open) remindLater();
        }}
      >
        <DialogContent
          lang={lang}
          className="flex max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl flex-col gap-4 rounded-lg"
          onOpenAutoFocus={(event) => {
            // Start screen readers and keyboard users at the heading, not the
            // first button.
            event.preventDefault();
            titleRef.current?.focus();
          }}
        >
          <DialogHeader className="space-y-3 pr-6 text-left">
            <div
              role="group"
              aria-label={t.languageLabel}
              className="bg-muted inline-flex w-fit rounded-md p-0.5"
            >
              {LANGUAGES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  lang={code}
                  aria-pressed={lang === code}
                  onClick={() => chooseLanguage(code)}
                  className={cn(
                    'focus-visible:ring-ring rounded px-3 py-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-2',
                    lang === code
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <DialogTitle
              ref={titleRef}
              tabIndex={-1}
              className="text-xl leading-snug focus:outline-none"
            >
              {t.title}
            </DialogTitle>
            <DialogDescription>{t.lead}</DialogDescription>
            {t.translationNote && (
              <p className="text-muted-foreground text-xs italic">{t.translationNote}</p>
            )}
          </DialogHeader>

          <div
            role="region"
            aria-label={t.rulesHeading}
            tabIndex={0}
            className="focus-visible:ring-ring min-h-0 flex-1 space-y-4 overflow-y-auto rounded-md border p-4 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2"
          >
            <section>
              <h3 className="mb-2 font-semibold">{t.rulesHeading}</h3>
              <p className="mb-2">{t.rulesIntro}</p>
              <ol className="list-decimal space-y-1 pl-5">
                {t.rules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ol>
            </section>
            <section>
              <h3 className="mb-2 font-semibold">{t.consequencesHeading}</h3>
              <p>{t.consequences}</p>
            </section>
            <section>
              <h3 className="mb-2 font-semibold">{t.aiHeading}</h3>
              <p>{t.ai}</p>
            </section>
          </div>

          <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
            <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <a
                href={notice.links.terms}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4"
              >
                {t.termsLink}
              </a>
              <a
                href={grievanceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-4"
              >
                {t.grievanceLink}
              </a>
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button variant="outline" onClick={remindLater} disabled={acknowledge.isPending}>
                {t.later}
              </Button>
              <Button onClick={() => acknowledge.mutate()} disabled={acknowledge.isPending}>
                {t.acknowledge}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
